import React from "react";
import styled, { css } from "styled-components";
import { $styles, IStyles } from "./$styles";
import { TheIcon } from "components/TheComponents";

const SelectInput = styled.input<IStyles>`
  color: #d2eaff;
  background: transparent;
  border: none;
  font-family: var(--font-family-golos);
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  letter-spacing: 0.02em;
  line-height: 150%;
  width: calc(100% - 20px);
  &:focus {
    border: none;
    outline: none;
  }
  &::placeholder {
    color: #8999ab;
    font-family: var(--font-family-golos);
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    letter-spacing: 0.02em;
    line-height: 150%;
  }

  &[data-selected="true"] {
    &::placeholder {
      color: white;
    }
  }

  &[data-truly-disabled="true"] {
    cursor: not-allowed;
    &::placeholder {
      color: var(--daylight-50-color);
    }
  }
  ${$styles}
`;

const Select = styled.div<IStyles & { inError?: boolean; errorText?: string }>`
  border: none;
  padding: 8px;
  border-radius: 8px;
  height: 48px;
  margin-bottom: 0;
  display: -webkit-box;
  display: -webkit-flex;
  display: -ms-flexbox;
  display: flex;
  -webkit-flex-direction: row;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-align-items: center;
  -webkit-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  gap: 6px;
  position: relative;
  width: 100%;
  background: var(--global-bg-dark-1);
  border: ${({ inError }) => (inError ? " 1px solid var(--color-error)" : "none")};

  ${({ errorText }) =>
    errorText &&
    css`
      &::after {
        width: 100%;
        height: 25px;
        left: 0;
        bottom: -25px;
        position: absolute;
        display: inline-block;
        color: var(--color-error);
        content: "${errorText}";
        white-space: nowrap;
        font-size: 11px;
      }
    `}

  &:hover {
    border-color: #3b8be9;
  }
  ${$styles}
`;

const SelectContainer = styled.div`
  position: relative;
`;

const Dropdown = styled.div`
  position: absolute;
  z-index: 1;
  background-color: var(--global-bg-dark-1);
  width: 100%;
  max-height: 200px;
  overflow-y: scroll;
  border: 1px solid rgba(36, 39, 70, 0.5);
`;

const DropdownItem = styled.div`
  height: 50px;
  padding: 15px;
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
  &:active {
    opacity: 0.5;
  }
`;

export type DropdownSelectProps<T> = {
  options: { label: string; value: T }[];
  onUpdateOptions?: () => void | Promise<void>;
  onSearch?: (value: string) => void | Promise<void>;
  selectedValue?: { label: string; value: T };
  onSelect?: (data: T) => void;
  customOption?: (
    key: string | number,
    option: { label: string; value: any },
    onSelect: (data: { label: string; value: any }) => void
  ) => React.ReactNode;
  key?: string;
  inError?: boolean;
  errorText?: string;
  disabled?: boolean;
};

const DropdownSelect: React.FC<DropdownSelectProps<any>> = ({
  options,
  onUpdateOptions,
  onSearch,
  onSelect,
  selectedValue,
  customOption,
  key,
  inError,
  errorText,
  disabled,
}) => {
  const [actualValue, setActualValue] = React.useState<{ label: string; value: any } | null>(
    selectedValue ?? { label: "Select...", value: null }
  );
  const [isOpenDropdown, setIsOpenDropdown] = React.useState<boolean>(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const [searchValue, setSearchValue] = React.useState<string>("");

  const toggleDropdown = () => {
    setIsOpenDropdown((prev) => !prev);
  };

  const handleScroll = React.useCallback(async () => {
    if (onUpdateOptions) {
      const dropdownContainer = dropdownRef.current;

      if (
        dropdownContainer &&
        dropdownContainer.scrollTop + dropdownContainer.clientHeight ===
          dropdownContainer.scrollHeight
      ) {
        await onUpdateOptions();
      }
    }
  }, [onUpdateOptions]);

  React.useEffect(() => {
    const dropdownContainer = dropdownRef.current;

    document.onclick = () => {
      setIsOpenDropdown(false);
    };

    if (dropdownContainer) {
      dropdownContainer.addEventListener("scroll", handleScroll);

      return () => {
        dropdownContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll, isOpenDropdown]);

  return (
    <SelectContainer key={key}>
      <Select
        errorText={errorText}
        inError={inError}
        onClick={(e) => {
          if (disabled) {
            return;
          }
          e.stopPropagation();
          toggleDropdown();
        }}
      >
        <SelectInput
          data-selected={!!actualValue?.value}
          placeholder={actualValue?.label}
          disabled={!Boolean(onSearch) || disabled}
          data-truly-disabled={!!disabled}
          value={searchValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchValue(e.target.value);
            onSearch?.(e.target.value);
            setIsOpenDropdown(true);
          }}
        />
        <TheIcon
          icon={isOpenDropdown ? "spoiler:up" : "spoiler:down"}
          styles={css`
            cursor: ${disabled ? "not-allowed" : "pointer"};
            width: 32px;
            height: 32px;
          `}
        />
      </Select>
      {isOpenDropdown && (
        <Dropdown ref={dropdownRef}>
          {options.map(
            (option: any, index: number) =>
              customOption?.(index, option, () => {
                setActualValue(option);
                onSelect?.(option);
                toggleDropdown();
                setSearchValue("");
              }) ?? (
                <DropdownItem
                  key={index}
                  onClick={() => {
                    setActualValue(option);
                    onSelect?.(option);
                    toggleDropdown();
                    setSearchValue("");
                  }}
                >
                  {option.label}
                </DropdownItem>
              )
          )}
        </Dropdown>
      )}
    </SelectContainer>
  );
};

export default DropdownSelect;
